import clsx from 'clsx';
import {
  SprdResponsiveImage,
  SprdResponsiveImageData,
  SprdResponsiveImageProps,
} from '@/components/ResponsiveImage/SprdResponsiveImage';
import type { BadgePosition, GalleryBadgeProps } from '../utility/galleryBadges';

export type ResponsiveImageDataWithBadges = SprdResponsiveImageData & {
  badges?: GalleryBadgeProps[];
};

export type GalleryImageProps = SprdResponsiveImageProps & {
  image: ResponsiveImageDataWithBadges;
  bottomOffsetDuration?: number;
  imageAltText?: string;
};

// [mobilePosition][desktopPosition] → position CSS classes for the <ul>
const BADGE_POSITION_CLASSES: Record<BadgePosition, Record<BadgePosition, string>> = {
  'top-left': {
    'top-left': 'top-2 left-2',
    'top-right': 'top-2 left-2 md:left-auto md:right-2',
    'bottom-left': 'top-2 left-2 md:top-auto md:bottom-2',
  },
  'top-right': {
    'top-left': 'top-2 right-2 md:right-auto md:left-2',
    'top-right': 'top-2 right-2',
    'bottom-left': 'top-2 right-2 md:top-auto md:bottom-2 md:right-auto md:left-2',
  },
  'bottom-left': {
    'top-left': 'bottom-2 left-2 md:bottom-auto md:top-2',
    'top-right': 'bottom-2 left-2 md:bottom-auto md:top-2 md:left-auto md:right-2',
    'bottom-left': 'bottom-2 left-2',
  },
};

// Color follows each badge's position and never changes on desktop
const BADGE_ITEM_COLOR_CLASSES: Record<BadgePosition, string> = {
  'top-left': 'bg-black text-white',
  'top-right': 'bg-black text-white',
  'bottom-left': 'bg-white text-black',
};

export const GalleryImage = ({
  image: { badges, ...imageData },
  bottomOffsetDuration = 0,
  imageAltText,
  ...rest
}: GalleryImageProps) => {
  const badgeGroups = new Map<
    string,
    { mobile: BadgePosition; desktop: BadgePosition; badges: GalleryBadgeProps[] }
  >();
  for (const badge of badges ?? []) {
    const desktop = badge.desktopPosition ?? badge.position;
    const group = badgeGroups.get(desktop);
    if (group) {
      group.badges.push(badge);
    } else {
      badgeGroups.set(desktop, { mobile: badge.position, desktop, badges: [badge] });
    }
  }

  return (
    <div className="relative size-full">
      <SprdResponsiveImage
        image={imageAltText ? { ...imageData, alt: imageAltText } : imageData}
        {...rest}
      />
      {Array.from(badgeGroups.values()).map(({ mobile, desktop, badges: groupBadges }) => (
        <BadgeList
          key={desktop}
          className={BADGE_POSITION_CLASSES[mobile][desktop]}
          badges={groupBadges}
          stacked={desktop === 'top-left'}
          offsetDuration={mobile === 'bottom-left' ? bottomOffsetDuration : 0}
        />
      ))}
    </div>
  );
};

const BadgeList = ({
  badges,
  className,
  stacked = false,
  offsetDuration = 0,
}: {
  className?: string;
  badges: GalleryBadgeProps[];
  stacked?: boolean;
  offsetDuration?: number;
}) => {
  return (
    <ul
      style={{ transitionDuration: `${offsetDuration}ms` }}
      className={clsx(
        'absolute flex flex-col gap-1 motion-safe:transition-transform motion-safe:ease-out',
        !stacked && 'xs:flex-row xs:flex-wrap xs:gap-2',
        offsetDuration > 0 ? '-translate-y-10' : 'translate-y-0',
        className
      )}
    >
      {badges.map((badge, index) => (
        <li
          key={index}
          className={clsx(
            'px-2 py-1 text-xs md:text-sm',
            badge.variant === 'gradient'
              ? 'bg-(image:--gradient-default) bg-size-[200%_100%] bg-left text-white'
              : BADGE_ITEM_COLOR_CLASSES[badge.position]
          )}
        >
          {badge.label}
        </li>
      ))}
    </ul>
  );
};
